/**
 * RN-09/RF-12 do PRD 32 — controle de quantidade de linhas da folha de monitoramento.
 *
 * A tabela nasce com 1 linha e mantém no mínimo 1 e no máximo `MAX_SHEET_ROWS` linhas,
 * qualquer que seja o modelo de folha aplicado — o modelo define exclusivamente as
 * colunas (RN-09 já vigente para colunas), quem dimensiona a folha em linhas é o
 * auditor. O teto é constante de produto, ajustável por configuração (por isso as
 * funções abaixo recebem `maxRows`/`minRows` como parâmetro opcional em vez de embutir
 * o valor) e **nunca** "sem limite" na ausência de configuração — 100 é o valor vigente
 * nesse caso.
 */

export const MAX_SHEET_ROWS = 100

/** A exclusão da última linha restante é bloqueada — a tabela nunca fica sem nenhuma. */
export const MIN_SHEET_ROWS = 1

/** RN-11 — motivo único, reusado nas três vias de bloqueio de inserção (RF-12). */
export function rowLimitReason(maxRows: number = MAX_SHEET_ROWS): string {
  return `Limite de ${maxRows} linhas atingido — exclua uma linha para adicionar outra.`
}

/** Motivo da exclusão bloqueada por deixar a folha abaixo do mínimo de linhas. */
export function minRowLimitReason(minRows: number = MIN_SHEET_ROWS): string {
  return minRows === 1
    ? 'A folha precisa manter ao menos 1 linha.'
    : `A folha precisa manter ao menos ${minRows} linhas.`
}

/** RN-09/RF-12 — se inserir `rowsToInsert` linhas ultrapassaria o teto configurado. */
export function canInsertRows(
  currentRowCount: number,
  rowsToInsert: number,
  maxRows: number = MAX_SHEET_ROWS
): boolean {
  return currentRowCount + rowsToInsert <= maxRows
}

/** RN-09/RF-12 — se excluir `rowsToDelete` linhas deixaria a folha abaixo do mínimo. */
export function canDeleteRows(
  currentRowCount: number,
  rowsToDelete: number,
  minRows: number = MIN_SHEET_ROWS
): boolean {
  return currentRowCount - rowsToDelete >= minRows
}

/**
 * RN-11 — o teto já foi atingido: gate de UI para "Adicionar linha" e para os itens de
 * inserção do menu de contexto, que ficam desabilitados enquanto isto for `true`.
 */
export function isAtRowLimit(currentRowCount: number, maxRows: number = MAX_SHEET_ROWS): boolean {
  return currentRowCount >= maxRows
}

/**
 * RF-12 — quantas linhas de um bloco colado a partir de `rowIndex` cabem no teto, e
 * quantas seriam descartadas. Não depende da quantidade atual de linhas: o jspreadsheet
 * CE cria as linhas que faltarem automaticamente até `rowIndex + pastedRowCount`, então
 * o teto é sobre esse destino final, não sobre a contagem de hoje.
 */
export function clampPastedRowSpan(
  rowIndex: number,
  pastedRowCount: number,
  maxRows: number = MAX_SHEET_ROWS
): { allowedRowCount: number; truncatedCount: number } {
  const capacity = Math.max(maxRows - rowIndex, 0)
  const allowedRowCount = Math.min(pastedRowCount, capacity)
  return { allowedRowCount, truncatedCount: pastedRowCount - allowedRowCount }
}
